import { c as _c } from "react/compiler-runtime";
// «Способы оплаты» по кадрам 4053:41958 / 4053:42294 (Айталы, 02.10): под
// заголовком тёмная кнопка «Добавить способ оплаты» (h40 r10 #2E2E2E, 14/18
// w600); ниже белые группы 366 r16 (p10, через 10) — «Карты», «СБП»,
// «Расчётный счёт»: заголовок 16/20 w600, строки 346×56 r16 #F8F8F8 через 12 —
// иконка в белом круге 36, подпись 14/18, корзина 20 справа. Пустую группу не
// показываем. Кнопка открывает окно «Новый способ оплаты» (440 r20, три серые
// кнопки 48 r12) → дальше прежние формы добавления карты, СБП и р/с.
// Под группами — ссылки на Баланс и реквизиты для оплаты по счёту: в кадре их
// нет, но это рабочие разделы кабинета (кошелёк чекаута, счёт ЮЛ).

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@osmix/ui/dialog";
import { Skeleton } from "@osmix/ui/skeleton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { api, type BuyerPaymentMethod } from "@/api";
import { BillingProfileForm } from "@/components/buyer/billing-profile-form";
import { SavedCardHint, savedCardExpiry, savedCardLabel } from "@/components/buyer/payment-method-form.tsx";
import { ErrorState } from "@/components/common/states";
import { CloseIcon, TrashIcon, WorkIcon } from "@/components/icons";
import { ProfilePanel } from "@/components/profile/profile-shell";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/text-field";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store";
const OVERLAY = "bg-black/20 backdrop-blur-[2px]";
type Kind = "card" | "sbp" | "company";
const kindOf = (m: BuyerPaymentMethod): Kind => m.provider === "sbp" ? "sbp" : m.provider === "company" ? "company" : "card";

// Группы на странице и варианты в окне выбора — в порядке макета.
const KINDS: Array<{
  kind: Kind;
  title: string;
  choice: string;
}> = [{
  kind: "card",
  title: "Карты",
  choice: "Банковская карта"
}, {
  kind: "sbp",
  title: "СБП",
  choice: "СБП"
}, {
  kind: "company",
  title: "Расчётный счёт",
  choice: "Расчетный счет"
}];
export function ProfilePaymentsPage() {
  const userId = useProfile(s => s.user?.id ?? "guest");
  const qc = useQueryClient();
  const key = ["buyer", userId, "payment-methods"];
  const methods = useQuery({
    queryKey: key,
    queryFn: () => api.buyer.listPaymentMethods()
  });
  // fd-payment-01: «Банковская карта» в окне выбора — только когда платёжная
  // система сохраняет карты (карта сохраняется при оплате, не формой).
  const payConfig = useQuery({
    queryKey: ["payments", "config"],
    queryFn: () => api.payments.config(),
    staleTime: 300_000
  });
  const savedCardsOn = payConfig.data?.savedCards === true;
  const [choosing, setChoosing] = useState(false);
  const [adding, setAdding] = useState<Kind | null>(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const reload = () => qc.invalidateQueries({
    queryKey: key
  });
  const remove = async (m: BuyerPaymentMethod) => {
    setBusyId(m.id);
    try {
      await api.buyer.deletePaymentMethod(m.id);
      toast.success("Способ оплаты удалён");
      await reload();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось удалить");
    } finally {
      setBusyId(null);
    }
  };
  const all = (methods.data ?? []).filter(m_0 => m_0.provider !== "balance");
  const groups = KINDS.map(k => ({
    ...k,
    rows: all.filter(m_1 => kindOf(m_1) === k.kind)
  })).filter(g => g.rows.length > 0);
  return <ProfilePanel title="Способы оплаты">
			{/* Тёмная кнопка M из DS; в кадре заливка #2E2E2E (вариант ds-ink-2). */}
			<Button size="md" onClick={() => setChoosing(true)} className="mb-3 self-start bg-ds-ink-2 hover:bg-ds-primary md:mb-4">
				Добавить способ оплаты
			</Button>
			{methods.isPending ? <div className="flex max-w-[366px] flex-col gap-2.5">
					{[0, 1].map(i => <Skeleton key={i} className="h-[176px] rounded-[16px] bg-white" />)}
				</div> : methods.isError ? <ErrorState onRetry={() => void methods.refetch()} /> : <div className="flex max-w-[366px] flex-col gap-2.5">
					{groups.length === 0 ? <p className="flex h-14 items-center rounded-[16px] bg-white px-4 text-[14px] leading-[18px] text-[#1F1F1F]/48">
							Пока ничего не добавлено
						</p> : groups.map(g_0 => <section key={g_0.kind} className="flex flex-col gap-3 rounded-[16px] bg-white p-2.5">
								<h2 className="text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#1F1F1F]">{g_0.title}</h2>
								<ul className="flex flex-col gap-3">
									{g_0.rows.map(m_2 => <li key={m_2.id} className="flex h-14 items-center gap-[11px] rounded-[16px] bg-[#F8F8F8] p-2.5">
											<span className="grid size-9 shrink-0 place-items-center rounded-full bg-white">
												<MethodIcon kind={g_0.kind} />
											</span>
											<span className="flex min-w-0 flex-1 flex-col">
												<span className="truncate text-[14px] leading-[18px] text-black">
													{methodLabel(m_2)}
													{m_2.isDefault && <span className="ml-2 text-[12px] text-[#1F1F1F]/48">основной</span>}
												</span>
												{g_0.kind === "card" && cardNote(m_2) && <span className="truncate text-[12px] leading-4 text-[#1F1F1F]/48" data-testid="card-note">
														{cardNote(m_2)}
													</span>}
											</span>
											<Button variant="ghost" size="icon-sm" aria-label={`Удалить ${methodLabel(m_2)}`} disabled={busyId === m_2.id} onClick={() => void remove(m_2)} className="hover:bg-white disabled:bg-transparent">
												<TrashIcon size={20} />
											</Button>
										</li>)}
								</ul>
							</section>)}
					<div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-[14px] text-[#1F1F1F]/64">
						<Link to="/profile/balance" className="rounded-sm outline-none hover:text-[#1F1F1F] hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
							Баланс OSMIX
						</Link>
						<button type="button" onClick={() => setBillingOpen(true)} className="rounded-sm outline-none hover:text-[#1F1F1F] hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">
							Реквизиты для оплаты по счёту
						</button>
					</div>
				</div>}

			<ChooseKindDialog open={choosing} savedCards={savedCardsOn} onClose={() => setChoosing(false)} onPick={kind => {
      setChoosing(false);
      setAdding(kind);
    }} />
			<AddCardDialog open={adding === "card"} onClose={() => setAdding(null)} />
			<AddSbpDialog open={adding === "sbp"} onClose={() => setAdding(null)} onAdded={reload} />
			<AddCompanyDialog open={adding === "company"} onClose={() => setAdding(null)} onAdded={reload} />
			<BillingDialog open={billingOpen} onOpenChange={setBillingOpen} />
		</ProfilePanel>;
}

// Окно «Новый способ оплаты» (кадр 4053:42294): 440 r20, заголовок 20/24 w700
// по центру и крестик справа; три кнопки 408×48 r12 #989AA2/12 %, 16/21 w600.
function ChooseKindDialog(t0) {
  const $ = _c(24);
  const {
    open,
    savedCards,
    onClose,
    onPick
  } = t0;
  let t1;
  if ($[0] !== onClose) {
    t1 = o => !o && onClose();
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      maxWidth: "min(440px, calc(100% - 2rem))"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DialogTitle className="text-center text-[20px] leading-6 font-bold tracking-[-0.6px] text-[#161616]">Новый способ оплаты</DialogTitle>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CloseIcon size={24} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== onClose) {
    t5 = <div className="relative flex h-14 items-center justify-center px-14">{t3}<button type="button" aria-label={"\u0417\u0430\u043A\u0440\u044B\u0442\u044C"} onClick={onClose} className="absolute top-4 right-4 grid size-6 place-items-center rounded-full outline-none hover:bg-ds-quaternary focus-visible:ring-2 focus-visible:ring-ds-focus">{t4}</button></div>;
    $[5] = onClose;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DialogDescription className="sr-only">Выберите, какой способ оплаты добавить</DialogDescription>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== onPick || $[9] !== savedCards) {
    let t8;
    if ($[11] !== savedCards) {
      t8 = k => k.kind !== "card" || savedCards;
      $[11] = savedCards;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== onPick) {
      t9 = k_0 => <Button key={k_0.kind} variant="gray" block={true} onClick={() => onPick(k_0.kind)}>{k_0.choice}</Button>;
      $[13] = onPick;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    t7 = KINDS.filter(t8).map(t9);
    $[8] = onPick;
    $[9] = savedCards;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <div className="flex flex-col gap-2.5 px-4 pt-2.5 pb-4">{t7}</div>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t5 || $[18] !== t8) {
    t9 = <DialogContent overlayClassName={OVERLAY} showCloseButton={false} onOpenAutoFocus={_temp} style={t2} className="gap-0 rounded-[20px] border-0 bg-white p-0 shadow-none sm:max-w-none">{t5}{t6}{t8}</DialogContent>;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== open || $[21] !== t1 || $[22] !== t9) {
    t10 = <Dialog open={open} onOpenChange={t1}>{t9}</Dialog>;
    $[20] = open;
    $[21] = t1;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function _temp(e) {
  return e.preventDefault();
}
const methodLabel = (m: BuyerPaymentMethod) => {
  if (m.provider === "sbp" || m.provider === "company") return m.label || m.brand;
  return savedCardLabel(m);
};

// Вторая строка карты (fd-payment-01): маска и срок от платёжной системы; у
// старой записи без токена — что оплатить ею нельзя.
const cardNote = (m: BuyerPaymentMethod): string => {
  if (!m.tokenKind) return "Не привязана к платёжной системе";
  return [m.cardMask, savedCardExpiry(m)].filter(Boolean).join(" · ");
};
function MethodIcon(t0) {
  const $ = _c(3);
  const {
    kind
  } = t0;
  if (kind === "sbp") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <SbpMark />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (kind === "company") {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <WorkIcon size={20} className="text-[#1F1F1F]" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Landmark className="size-5 text-[#1F1F1F]" strokeWidth={1.5} />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

// Знак СБП (восемь цветных треугольников, как в кадре).
function SbpMark() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 14 17" className="h-[17px] w-[14px]" aria-hidden="true"><path d="M0 3.5 1.94 6.9v2.2L0 12.5Z" fill="#5B57A2" /><path d="m7.44 5.7 1.82-1.1h3.71L7.44 8Z" fill="#D90751" /><path d="m7.43 3.5.01 4.5-1.94-1.1V0Z" fill="#FAB718" /><path d="M12.97 4.6H9.25L7.43 3.5 5.5 0Z" fill="#ED6F26" /><path d="M7.44 12.5v-2.2L5.5 9.1V16Z" fill="#63B22F" /><path d="M9.25 11.4 1.94 6.9 0 3.5l12.97 7.9Z" fill="#1487C9" /><path d="m5.5 16 1.94-3.5 1.81-1.1h3.72Z" fill="#017F36" /><path d="M0 12.5 5.51 9.1 3.66 8 1.94 9.1Z" fill="#984995" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ModalShell(t0) {
  const $ = _c(23);
  const {
    open,
    onOpenChange,
    title,
    children,
    width: t1
  } = t0;
  const width = t1 === undefined ? 366 : t1;
  const t2 = `min(${width}px, calc(100% - 2rem))`;
  let t3;
  if ($[0] !== t2) {
    t3 = {
      maxWidth: t2
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== title) {
    t4 = <DialogTitle className="ds-h3 text-ds-ink">{title}</DialogTitle>;
    $[2] = title;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== onOpenChange) {
    t5 = () => onOpenChange(false);
    $[4] = onOpenChange;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CloseIcon size={24} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5) {
    t7 = <button type="button" aria-label={"\u0417\u0430\u043A\u0440\u044B\u0442\u044C"} onClick={t5} className="grid size-7 place-items-center rounded-full outline-none hover:bg-ds-quaternary focus-visible:ring-2 focus-visible:ring-ds-focus">{t6}</button>;
    $[7] = t5;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t4 || $[10] !== t7) {
    t8 = <div className="flex items-center justify-between gap-3">{t4}{t7}</div>;
    $[9] = t4;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== title) {
    t9 = <DialogDescription className="sr-only">{title}</DialogDescription>;
    $[12] = title;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== children || $[15] !== t3 || $[16] !== t8 || $[17] !== t9) {
    t10 = <DialogContent overlayClassName={OVERLAY} showCloseButton={false} onOpenAutoFocus={_temp2} style={t3} className="max-h-[calc(100dvh-2rem)] gap-3 overflow-y-auto rounded-[20px] border-0 bg-white p-4 pt-[18px] shadow-none sm:max-w-none">{t8}{t9}{children}</DialogContent>;
    $[14] = children;
    $[15] = t3;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== onOpenChange || $[20] !== open || $[21] !== t10) {
    t11 = <Dialog open={open} onOpenChange={onOpenChange}>{t10}</Dialog>;
    $[19] = onOpenChange;
    $[20] = open;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}

// «Банковская карта» (fd-payment-01): своей формы ввода карты нет — окно
// объясняет, что карта сохраняется при оплате заказа.
function _temp2(e) {
  return e.preventDefault();
}
function AddCardDialog(t0) {
  const $ = _c(9);
  const {
    open,
    onClose
  } = t0;
  let t1;
  if ($[0] !== onClose) {
    t1 = o => !o && onClose();
    $[0] = onClose;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SavedCardHint />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClose) {
    t3 = <Button block={true} onClick={onClose}>Понятно</Button>;
    $[3] = onClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== open || $[6] !== t1 || $[7] !== t3) {
    t4 = <ModalShell open={open} onOpenChange={t1} title={"\u0411\u0430\u043D\u043A\u043E\u0432\u0441\u043A\u0430\u044F \u043A\u0430\u0440\u0442\u0430"}>{t2}{t3}</ModalShell>;
    $[5] = open;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function AddSbpDialog({
  open,
  onClose,
  onAdded
}: {
  open: boolean;
  onClose: () => void;
  onAdded: () => Promise<unknown>;
}) {
  const banks = useQuery({
    queryKey: ["sbp-banks"],
    queryFn: () => api.buyer.listSbpBanks(),
    enabled: open,
    staleTime: 3_600_000
  });
  const [bankId, setBankId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!bankId) return;
    setBusy(true);
    try {
      await api.buyer.createSbpPaymentMethod({
        bankId
      });
      toast.success("Банк для оплаты по СБП привязан");
      await onAdded();
      setBankId(null);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось привязать СБП");
    } finally {
      setBusy(false);
    }
  };
  return <ModalShell open={open} onOpenChange={o => !o && onClose()} title="Привязать СБП">
			<p className="text-[14px] text-[#1F1F1F]/64">Выберите банк, через который будете платить по QR-коду СБП.</p>
			<ul className="flex flex-col gap-2">
				{(banks.data ?? []).map(b => <li key={b.id}>
						<button type="button" onClick={() => setBankId(b.id)} aria-pressed={bankId === b.id} className={cn("flex h-12 w-full items-center gap-3 rounded-[16px] border-[0.5px] px-2.5 text-left text-[14px] text-[#1F1F1F] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40", bankId === b.id ? "border-[#FA5C2E] bg-[#FA5C2E]/5" : "border-transparent bg-[#F8F8F8] hover:bg-[#F2F2F2]")}>
							<span className="grid size-8 place-items-center rounded-full bg-white">
								<SbpMark />
							</span>
							{b.name}
						</button>
					</li>)}
				{banks.isPending && [0, 1, 2].map(i => <Skeleton key={i} className="h-12 rounded-[16px]" />)}
			</ul>
			<Button block disabled={!bankId || busy} onClick={() => void save()}>
				Привязать
			</Button>
		</ModalShell>;
}
function AddCompanyDialog({
  open,
  onClose,
  onAdded
}: {
  open: boolean;
  onClose: () => void;
  onAdded: () => Promise<unknown>;
}) {
  const [inn, setInn] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = /^\d{10}(\d{2})?$/.test(inn);
  const save = async () => {
    setBusy(true);
    try {
      await api.buyer.createCompanyPaymentMethod({
        inn,
        ...(label.trim() ? {
          label: label.trim()
        } : {})
      });
      toast.success("Расчётный счёт добавлен");
      await onAdded();
      setInn("");
      setLabel("");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось добавить расчётный счёт");
    } finally {
      setBusy(false);
    }
  };
  return <ModalShell open={open} onOpenChange={o => !o && onClose()} title="Новый расчётный счёт">
			<TextField size="lg" label="Название организации" value={label} onChange={e_0 => setLabel(e_0.target.value)} placeholder="ООО «Ромашка»" />
			<TextField size="lg" label="ИНН" value={inn} onChange={e_1 => setInn(e_1.target.value.replace(/\D/g, "").slice(0, 12))} inputMode="numeric" placeholder="10 или 12 цифр" />
			<Button block disabled={!valid || busy} onClick={() => void save()}>
				Сохранить
			</Button>
		</ModalShell>;
}

// Реквизиты ЮЛ для оплаты по счёту (fd-payment-02) — прежняя форма кабинета.
function BillingDialog(t0) {
  const $ = _c(15);
  const {
    open,
    onOpenChange
  } = t0;
  const userId = useProfile(_temp3);
  const qc = useQueryClient();
  let t1;
  if ($[0] !== userId) {
    t1 = ["buyer", userId, "billing"];
    $[0] = userId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== open || $[3] !== t1) {
    t2 = {
      queryKey: t1,
      queryFn: _temp4,
      enabled: open
    };
    $[2] = open;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const billing = useQuery(t2);
  let t3;
  if ($[5] !== billing.data || $[6] !== billing.isPending || $[7] !== onOpenChange || $[8] !== qc || $[9] !== userId) {
    t3 = billing.isPending ? <Skeleton className="h-64 rounded-[16px]" /> : <BillingProfileForm profile={billing.data ?? null} onSaved={async () => {
      toast.success("\u0420\u0435\u043A\u0432\u0438\u0437\u0438\u0442\u044B \u0441\u043E\u0445\u0440\u0430\u043D\u0435\u043D\u044B");
      await qc.invalidateQueries({
        queryKey: ["buyer", userId, "billing"]
      });
      onOpenChange(false);
    }} />;
    $[5] = billing.data;
    $[6] = billing.isPending;
    $[7] = onOpenChange;
    $[8] = qc;
    $[9] = userId;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== onOpenChange || $[12] !== open || $[13] !== t3) {
    t4 = <ModalShell open={open} onOpenChange={onOpenChange} title={"\u0420\u0435\u043A\u0432\u0438\u0437\u0438\u0442\u044B \u0434\u043B\u044F \u0441\u0447\u0451\u0442\u0430"} width={560}>{t3}</ModalShell>;
    $[11] = onOpenChange;
    $[12] = open;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function _temp4() {
  return api.buyer.getBillingProfile();
}
function _temp3(s) {
  return s.user?.id ?? "guest";
}